import dayjs from 'dayjs';
import { getters } from '/@/modules/core/app/helpers/store';
import { BaseService } from '/@/modules/core/app/services/abstracts/BaseService';
import FinancialAccountResource from '/@/modules/financialaccount/resources/FinancialAccountResource';
import ConnectedInstitutionsService from '/@/modules/financialaccount/services/ConnectedInstitutionsService';
import CacheHelper from '/@/modules/core/app/helpers/CacheHelper';
import EventBus from '/@/modules/core/app/helpers/EventBus';
import { FinancialAccountUpdateBalancesEvent } from '/@/modules/financialaccount/constants/FinancialAccountUpdateBalancesEvent';
import Logger from '/@/modules/core/app/helpers/Logger';

export class FinancialAccountBalanceService extends BaseService {
  /**
   * @var {FinancialAccountResource}
   */
  resource;

  /**
   * in milliseconds, time to refresh accounts balances
   * @type {number}
   */
  refreshRate = 1000 * 60 * 30; // every 30 minutes

  /**
   * @type {string}
   */
  CACHE_KEY_UPDATE_BALANCES = 'cache_key_update_balances';

  constructor() {
    super();
    this.resource = FinancialAccountResource;
  }

  /**
   * @returns {Promise<boolean>}
   */
  async shouldUpdateBalances() {
    if (!CacheHelper.get(this.CACHE_KEY_UPDATE_BALANCES)) {
      return this.hasConnectedFinancialInstitutions();
    }

    return false;
  }

  /**
   * @returns {boolean}
   */
  async updateBalances() {
    EventBus.signal(FinancialAccountUpdateBalancesEvent.START);

    return this.update();
  }

  /**
   * @returns {boolean}
   */
  async updateBalancesSilently(financialInstitution = null) {
    return this.update(financialInstitution);
  }

  async update(financialInstitution = null) {
    try {
      const response = await this.resource.updateBalances(financialInstitution);
      await ConnectedInstitutionsService.getConnectedInstitutions();
      CacheHelper.set(this.CACHE_KEY_UPDATE_BALANCES, true, dayjs().add(6, 'hours').unix());
      return response;
    } catch (e) {
      Logger.log(e.message);
    } finally {
      EventBus.signal(FinancialAccountUpdateBalancesEvent.END);
    }

    return false;
  }

  /**
   * @param {FinancialAccount} financialAccount
   * @returns {*}
   */
  updateBalance(financialAccount) {
    return this.resource.updateBalance(financialAccount);
  }

  async startBackgroundSync() {
    if (await this.shouldUpdateBalances()) {
      await this.updateBalances();
    }
    this.syncUpdateBalances();
  }

  syncUpdateBalances() {
    setTimeout(async () => {
      const update = await this.shouldUpdateBalances();
      if (!update) {
        return this.syncUpdateBalances();
      }

      try {
        this.updateBalances();
      } catch (e) {
        Logger.log(e.message);
      } finally {
        this.syncUpdateBalances();
      }
    }, this.refreshRate);
  }

  /**
   * @returns {Promise<boolean>}
   */
  async hasConnectedFinancialInstitutions() {
    if (!getters.auth.getUser()?.hasCurrentEstate) {
      return false;
    }

    if (getters.financialaccount.getConnectedFinancialInstitutions()?.length) {
      return true;
    }

    const institutions = await ConnectedInstitutionsService.getConnectedInstitutions();

    return !!institutions.length;
  }
}

export default new FinancialAccountBalanceService();
